import { List, ListItem, ListItemText, Menu, TextField, Chip, Divider } from '@mui/material';
import React, { FC, memo, useState, useEffect, useCallback } from 'react';
import { TRUCK_UNSET_LABEL } from 'src/constants/assignmentLabels';
import numberDecorator from 'src/decorators/number.decorator';

export type SelectTruckMenuTruckEntity = {
  id: number;
  licensePlateValue: string;
  truckNumber: string;
  maximumLoadingCapacityWeightForCalculation: number;
  loadingPlatformVolumeMm3: number;
  temporary: boolean;
}

type Props = {
  truckAnchorEl: HTMLElement | null;
  onClose: (event: MouseEvent) => void;
  onTruckClick: (event: React.MouseEvent, id: number, licensePlateValue: string) => void;
  truckData: SelectTruckMenuTruckEntity[];
  truckIdsHasDelivery: number[];
  isTemporary: boolean;
};

const SelectTruckMenuPresenter: FC<Props> = memo(({
  truckAnchorEl,
  onClose,
  onTruckClick,
  truckData,
  truckIdsHasDelivery,
  isTemporary,
}) => {
  const [searchTruckInputValue, setSearchTruckInputValue] = useState('');
  const [searchTruckQuery, setSearchTruckQuery] = useState<string>('');
  const [isComposing, setIsComposing] = useState(false);

  const handleTruckSearchQueryChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTruckInputValue(event.target.value);
  }, []);

  useEffect(() => {
    if (isComposing) return;
    setSearchTruckQuery(searchTruckInputValue);
  }, [isComposing, searchTruckInputValue]);

  const filteredTrucks = truckData?.filter(
    (it) => !it.temporary && it.licensePlateValue.toLocaleLowerCase().includes(searchTruckQuery?.toLowerCase())
  ) ?? [];

  const truckDisplayValue = useCallback((truck: SelectTruckMenuTruckEntity) => {
    if (!truck) return '';

    const weightAndVolume = [
      numberDecorator.convertGramToKg(truck.maximumLoadingCapacityWeightForCalculation),
      numberDecorator.convertMm3ToM3(truck.loadingPlatformVolumeMm3),
    ].filter((it) => it).join('/');
    return [
      truck.licensePlateValue,
      weightAndVolume ? `(${weightAndVolume})` : '',
    ].join(' ');
  }, []);

  const noPlan = <Chip color="error" size="small" label="計画無" />;

  return (
    <Menu
      anchorEl={truckAnchorEl}
      open={Boolean(truckAnchorEl)}
      onClose={onClose}
      sx={{ maxHeight: 500, }}
      slotProps={{
        paper: {
          sx: { padding: 0, margin: 0 }
        }
      }}
    >
      <TextField
        fullWidth
        variant="outlined"
        size="small"
        placeholder="検索"
        value={searchTruckInputValue}
        onChange={handleTruckSearchQueryChange}
        onCompositionStart={() => setIsComposing(true)}
        onCompositionEnd={() => setIsComposing(false)}
      />
      <List sx={{ p: 0, m: 0, }}>
        {!isTemporary && [
          <ListItem
            key={['truck', '-1'].join('-')}
            component="div"
            onClick={(event: React.MouseEvent) => onTruckClick(event, -1, TRUCK_UNSET_LABEL)}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <ListItemText
              primary="車両未設定にする"
              slotProps={{
                primary: { sx: { fontSize: 14, color: 'black' } }
              }}
            />
          </ListItem>,
          <Divider key="unassign-divider" />,
        ]}
        {filteredTrucks.map((truck) => (
          <ListItem
            key={['truck', truck.id].join('-')}
            component="div"
            onClick={(event: React.MouseEvent) => onTruckClick(event, truck.id, truck.licensePlateValue)}
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              cursor: 'pointer',
              '&:hover': {
                backgroundColor: 'action.hover',
              },
            }}
          >
            <ListItemText
              primary={truckDisplayValue(truck)}
              secondary={truck.truckNumber}
              slotProps={{
                primary: { sx: { fontSize: 14, color: 'black' } },
                secondary: { sx: { fontSize: 12 } }
              }}
            />
            {!truckIdsHasDelivery.includes(truck.id) && noPlan}
          </ListItem>
        ))}
      </List>
    </Menu>
  );
});

export default SelectTruckMenuPresenter;
